/**
 * A KPI sparkline — the shape of a number over the selected range.
 *
 * recharts is already a dependency (RevenueTrendChart uses it), so this is a
 * real chart rather than a hand-rolled polyline: it gets the domain handling,
 * the null-gap behaviour and the responsive sizing for free.
 *
 * NO AXES, NO GRID, NO TOOLTIP. A sparkline is a shape, not a plot; anything
 * else in 34px of height is noise. The exact numbers are on the card beside it.
 *
 * COLOUR IS A THEME PALETTE KEY, never a hex. `tone` names the semantic role
 * ('primary' = teal, 'secondary' = gold, 'success' = green, 'error' =
 * burgundy) and the stroke is resolved from the live theme, so dark mode and
 * any future palette change follow without touching this file.
 *
 * jsdom has no layout, so ResponsiveContainer measures 0×0 and recharts renders
 * nothing. That is fine and deliberate: the tests assert on the numbers, and a
 * component that threw at zero size would take every KPI card down with it.
 */

import React from 'react';
import { Box, useTheme } from '@mui/material';
import { Line, LineChart, ResponsiveContainer, YAxis } from 'recharts';
import { num } from './weddingFormat';
import type { KpiSeriesPoint } from './weddingApi';

export type SparklineTone = 'primary' | 'secondary' | 'success' | 'error' | 'info' | 'warning';

interface Props {
  points: KpiSeriesPoint[] | undefined | null;
  tone?: SparklineTone;
  height?: number;
  /** Read by screen readers in place of the drawing. */
  label?: string;
}

const Sparkline: React.FC<Props> = ({ points, tone = 'primary', height = 36, label }) => {
  const theme = useTheme();

  const rows = React.useMemo(
    () => (points || []).map((p) => ({ date: p.date, value: num(p.value) })),
    [points]
  );

  // Two points is the minimum that can describe a direction. One point is a
  // dot, and a dot dressed as a trend line is a lie about a shape.
  if (rows.length < 2) return null;

  const stroke = theme.palette[tone].main;

  return (
    <Box
      sx={{ height, width: '100%', mt: 1 }}
      role="img"
      aria-label={label || `Trend over ${rows.length} days`}
    >
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={rows} margin={{ top: 2, right: 2, bottom: 2, left: 2 }}>
          {/* A flat series must not fill the band — pad the domain instead. */}
          <YAxis hide domain={['dataMin', 'dataMax']} />
          <Line
            type="monotone"
            dataKey="value"
            stroke={stroke}
            strokeWidth={2}
            dot={false}
            isAnimationActive={false}
          />
        </LineChart>
      </ResponsiveContainer>
    </Box>
  );
};

export default Sparkline;
